<template>
  <v-card flat class="rmr-table-shell">
    <div class="rmr-table-shell__header">
      <div class="roles-filters">
        <rmr-text-field
          v-model="search"
          :append-icon="icons.mdiMagnify"
          label="Recherche textuelle"
          clearable
        />
        <rmr-autocomplete
          v-model="selectedRoles"
          :items="items"
          item-text="name"
          item-value="id"
          label="Filtrer par rôles"
          multiple
          chips
          deletable-chips
          clearable
        />
        <rmr-autocomplete
          v-model="selectedPermissions"
          :items="allPermissions"
          item-text="displayName"
          item-value="id"
          label="Filtrer par permissions"
          multiple
          chips
          deletable-chips
          clearable
        />
      </div>
    </div>

    <v-card-text class="rmr-table-shell__body">
      <v-data-table
        locale="fr-FR"
        :headers="headers"
        :items="filteredItems"
        :sort-by="'updated'"
        :sort-desc="true"
        :loading="loading"
        class="rmr-data-table"
        item-key="id"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
      >
        <template v-slot:loading>
          <div class="rmr-data-table__state">
            <v-progress-circular indeterminate color="#6366F1" size="28" class="mb-2" />
            <div class="rmr-data-table__state-title">Chargement des rôles</div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="rmr-data-table__state">
            <div class="rmr-data-table__state-title">
              {{ hasActiveFilters ? "Aucun rôle trouvé" : "Aucun rôle disponible" }}
            </div>
            <div class="rmr-data-table__state-text">
              {{
                hasActiveFilters
                  ? "Aucun rôle ne correspond aux filtres sélectionnés."
                  : "Créez un premier rôle pour structurer les permissions métier."
              }}
            </div>
          </div>
        </template>

        <template v-slot:item.permissions="{ item }">
          <rmr-keyword-chips
            v-if="permissionLabels(item).length"
            :keywords="permissionLabels(item)"
            :max-visible="3"
          />
          <span v-else class="text-caption grey--text text--darken-1">
            Aucune
          </span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex" style="gap: 8px">
            <rmr-icon-button
              :icon="icons.mdiPencil"
              variant="edit"
              tooltip="Modifier le rôle"
              @click="$emit('edit', item)"
            />
            <rmr-icon-button
              :icon="icons.mdiTrashCan"
              variant="delete"
              tooltip="Supprimer le rôle"
              @click="
                dialogModel.dialog = true;
                dialogModel.id = item.id;
                dialogModel.entryName = item.name;
              "
            />
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <rmr-confirm-dialog
      v-model="dialogModel.dialog"
      title="Confirmation Suppression"
      :message="`Le role '${dialogModel.entryName}' et toutes ses references associees seront supprimes.`"
      confirm-label="Supprimer"
      cancel-label="Annuler"
      danger
      @confirm="deleteEntry"
    />
  </v-card>
</template>

<script>
import axios from "@axios";
import store from "@/store";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrKeywordChips from "@/components/ui/feedback/RmrKeywordChips.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import { mdiPencil, mdiTrashCan, mdiMagnify } from "@mdi/js";

export default {
  name: "RolesTable",
  components: {
    RmrConfirmDialog,
    RmrAutocomplete,
    RmrIconButton,
    RmrKeywordChips,
    RmrTextField,
  },
  async created() {
    await Promise.all([
      this.fetchPermissions(),
      this.updateData()
    ]);
  },
  data() {
    return {
      search: "",
      selectedRoles: [],
      selectedPermissions: [],
      items: [],
      allPermissions: [],
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 25,
      },
      dialogModel: {
        dialog: false,
        id: -1,
        entryName: "",
      },
      headers: [
        { text: "ID", value: "id", width: "80px" },
        { text: "Nom", align: "start", value: "name" },
        { text: "Description", value: "description" },
        { text: "Permissions", value: "permissions", sortable: false },
        { text: "Actions", value: "actions", sortable: false, width: "120px" },
      ],
      icons: {
        mdiPencil,
        mdiTrashCan,
        mdiMagnify,
      },
    };
  },
  computed: {
    permissionsMap() {
      const map = {};
      this.allPermissions.forEach(p => {
        map[p.id] = `${p.subject}_${p.action}`;
      });
      return map;
    },
    filteredItems() {
      return this.items.filter(item => {
        // Filtre texte (nom ou description)
        const searchText = this.search ? this.search.toLowerCase() : "";
        const matchesSearch = !searchText || 
          (item.name && item.name.toLowerCase().includes(searchText)) ||
          (item.description && item.description.toLowerCase().includes(searchText));

        if (!matchesSearch) return false;

        // Filtre par rôles sélectionnés
        const matchesRoles = this.selectedRoles.length === 0 || 
          this.selectedRoles.includes(item.id);
        
        if (!matchesRoles) return false;

        // Filtre par permissions sélectionnées (Logique OR : doit avoir au moins une des permissions sélectionnées)
        if (this.selectedPermissions.length > 0) {
          if (!item.permissions) return false;
          const rolePerms = item.permissions.split(',').map(id => parseInt(id));
          const hasAnySelectedPerms = this.selectedPermissions.some(pId => rolePerms.includes(pId));
          if (!hasAnySelectedPerms) return false;
        }

        return true;
      });
    },
    hasActiveFilters() {
      return Boolean(
        this.search ||
        this.selectedRoles.length ||
        this.selectedPermissions.length
      );
    },
  },
  methods: {
    permissionLabels(item) {
      if (!item.permissions) {
        return [];
      }

      return item.permissions
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter(Boolean)
        .map((id) => this.permissionsMap[id] || `ID: ${id}`);
    },
    async fetchPermissions() {
      try {
        const { data } = await axios.get("/permissions/");
        this.allPermissions = data.items.map(p => ({
          ...p,
          displayName: `${p.subject}_${p.action}`
        }));
      } catch (e) {
        console.error("Erreur lors de la récupération des permissions", e);
      }
    },
    async updateData() {
      this.loading = true;
      try {
        const { data } = await axios.get("/roles/");
        this.items = data.items;
      } catch (e) {
        console.error("Erreur lors de la récupération des rôles", e);
      } finally {
        this.loading = false;
      }
    },
    async deleteEntry() {
      const { status } = await axios.delete(`/roles/${this.dialogModel.id}`);
      if (status === 204) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Le role '${this.dialogModel.entryName}' et toutes ses references ont ete supprimes.`,
          color: "success",
          timeout: 4000,
        });
        this.dialogModel.dialog = false;
        this.updateData();
      }
    },
    async reload() {
      await Promise.all([this.fetchPermissions(), this.updateData()]);
    },
  },
};
</script>

<style lang="scss" scoped>
.roles-filters {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

@media (max-width: 960px) {
  .roles-filters {
    grid-template-columns: 1fr;
  }
}
</style>
